/**
 * Mana Locals Admin — the AI responses' ENVELOPES.
 *
 * CONTRACT SOURCE: the AI contract published by the API repository. Every response
 * the AI operations name arrives in the API's correlation envelope `{ data, meta }`,
 * and `src/api/client/http.ts` parses the RAW body — so a schema that described
 * only the payload would reject the API's own answer, and the console would report
 * a contract mismatch for a response that was perfectly correct.
 *
 * These wrappers are the boundary. The payload shapes live in `ai.ts`; the services
 * unwrap `data` and hand the screens the payload.
 *
 * The `meta` half is the API's `MetaSchema` (`{ requestId }`), the same one every
 * other module carries.
 */
import { z } from 'zod';
import { ResponseMetaSchema } from '@/api/schemas/common';
import {
  AiUsageSummarySchema,
  EffectiveAiSettingsSchema,
  PlatformAiSettingsSchema,
  ProviderSummarySchema,
  ProviderTestResultSchema,
  TenantAiAvailabilitySchema,
} from '@/api/schemas/ai';

/* --------------------------------------------------------------------------- */
/* THE ENVELOPES                                                                */
/* --------------------------------------------------------------------------- */

/**
 * Every AI answer arrives in the API's correlation envelope: `{ data, meta }`.
 *
 * `src/api/client/http.ts` parses the RAW body, so a schema that described only
 * the payload would reject the API's own answer — and the console would report a
 * contract mismatch for a response that was perfectly correct. These wrappers are
 * the boundary; the service unwraps `data` and hands the screens the payload.
 *
 * The `meta` half is the API's `MetaSchema` (`{ requestId }`), the same one every
 * other module carries.
 */

/** One provider, in its envelope. */
export const ProviderDetailResponseSchema = z.object({
  data: ProviderSummarySchema,
  meta: ResponseMetaSchema,
});

export type ProviderDetailResponse = z.infer<typeof ProviderDetailResponseSchema>;

/** The provider collection, in its envelope. */
export const ProviderListResponseSchema = z.object({
  data: z.object({ items: z.array(ProviderSummarySchema) }),
  meta: ResponseMetaSchema,
});

export type ProviderListResponse = z.infer<typeof ProviderListResponseSchema>;

/**
 * What a credential write reports.
 *
 * The STATUS, never the value: whether a credential is configured and which key
 * version is live. There is no field here for the key itself, and none may be
 * added.
 */
export const CredentialWriteResultSchema = z.object({
  credentialConfigured: z.boolean(),
  keyVersion: z.number().int().positive().nullable(),
});

export type CredentialWriteResult = z.infer<typeof CredentialWriteResultSchema>;

/** The credential write, in its envelope. */
export const CredentialWriteResponseSchema = z.object({
  data: CredentialWriteResultSchema,
  meta: ResponseMetaSchema,
});

export type CredentialWriteResponse = z.infer<typeof CredentialWriteResponseSchema>;

/** The connection test, in its envelope. */
export const ProviderTestResponseSchema = z.object({
  data: ProviderTestResultSchema,
  meta: ResponseMetaSchema,
});

export type ProviderTestResponse = z.infer<typeof ProviderTestResponseSchema>;

/** The deployment's AI settings, in their envelope. */
export const PlatformAiSettingsResponseSchema = z.object({
  data: PlatformAiSettingsSchema,
  meta: ResponseMetaSchema,
});

export type PlatformAiSettingsResponse = z.infer<typeof PlatformAiSettingsResponseSchema>;

/** One tenant's AI availability, in its envelope. */
export const TenantAiAvailabilityResponseSchema = z.object({
  data: TenantAiAvailabilitySchema,
  meta: ResponseMetaSchema,
});

export type TenantAiAvailabilityResponse = z.infer<typeof TenantAiAvailabilityResponseSchema>;

/** The tenant availability collection, in its envelope. */
export const TenantAiAvailabilityListResponseSchema = z.object({
  data: z.object({ items: z.array(TenantAiAvailabilitySchema) }),
  meta: ResponseMetaSchema,
});

export type TenantAiAvailabilityListResponse = z.infer<typeof TenantAiAvailabilityListResponseSchema>;

/** The tenant's effective AI configuration, in its envelope. */
export const EffectiveAiSettingsResponseSchema = z.object({
  data: EffectiveAiSettingsSchema,
  meta: ResponseMetaSchema,
});

export type EffectiveAiSettingsResponse = z.infer<typeof EffectiveAiSettingsResponseSchema>;

/** The tenant's usage, in its envelope. */
export const AiUsageResponseSchema = z.object({
  data: AiUsageSummarySchema,
  meta: ResponseMetaSchema,
});

export type AiUsageResponse = z.infer<typeof AiUsageResponseSchema>;
